import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useCanAccessBookkeeping from '~/hooks/useCanAccessBookkeeping';
import { useHasBilancioCee, useIsPayrollConsultant } from '~/hooks/useFeatureFlag';
import useRouteMatch from '~/hooks/useRouteMatch';
import FinancialAdvisorIcon from '~/icons/icon-user-financial-advisor.svg?react';
import { RouteConfig } from '~/routes/routeConfig';
import { useIsSubmenuOpen, useSetSubmenu } from '~/state/navigationSidebar';
import { SubmenuSection } from '~/types/sidebar';
import NavigationSidebarItem from '../NavigationSidebarItem';

const BookkeepingItem: React.FC = () => {
  const { t } = useTranslation('sidebar', { keyPrefix: 'bookkeeping' });
  const canAccess = useCanAccessBookkeeping();
  const isPayrollConsultant = useIsPayrollConsultant();
  const hasBilancioCee = useHasBilancioCee();
  const isSubmenuOpen = useIsSubmenuOpen('bookkeeping');
  const openSubmenu = useSetSubmenu('bookkeeping');

  const sections: SubmenuSection[] = useMemo(
    () =>
      isPayrollConsultant
        ? [
            {
              label: t('submenu.general_bookkeeping'),
              items: [
                {
                  label: t('submenu.non_electronic_documents'),
                  href: RouteConfig.GeneralBookkeepingNonElectronicDocuments.buildLink(),
                },
              ],
            },
          ]
        : [
            {
              label: t('submenu.VAT_bookkeeping'),
              items: [
                { label: t('submenu.documents_bills'), href: RouteConfig.BookkeepingDocuments.buildLink() },
                {
                  label: t('submenu.books'),
                  href: RouteConfig.BookkeepingBooks.buildLink(),
                },
                {
                  label: t('submenu.settlement'),
                  href: RouteConfig.BookkeepingVATSettlement.buildLink(),
                },
                {
                  label: t('submenu.lipe'),
                  href: RouteConfig.BookkeepingLIPE.buildLink(),
                },
              ],
            },
            {
              label: t('submenu.general_bookkeeping'),
              items: [
                {
                  label: t('submenu.transactions'),
                  href: RouteConfig.GeneralBookkeepingTransactions.buildLink(),
                },
                {
                  label: t('submenu.non_electronic_documents'),
                  href: RouteConfig.GeneralBookkeepingNonElectronicDocuments.buildLink(),
                },
                {
                  label: t('submenu.cash_book'),
                  href: RouteConfig.GeneralBookkeepingCashBook.buildLink(),
                },
                {
                  label: t('submenu.ledger_cards'),
                  href: RouteConfig.GeneralBookkeepingLedgerCards.buildLink(),
                },
                {
                  label: t('submenu.provisional_balance'),
                  href: RouteConfig.GeneralBookkeepingProvisionalBalance.buildLink(),
                },
                {
                  label: t('submenu.fixed_assets'),
                  href: RouteConfig.GeneralBookkeepingFixedAssets.buildLink(),
                },
              ],
            },
            {
              label: t('submenu.adempimenti'),
              items: [
                {
                  label: t('submenu.withholding_tax'),
                  href: RouteConfig.BookkeepingWithholdingTax.buildLink(),
                },
                {
                  label: t('submenu.intrastat'),
                  href: RouteConfig.BookkeepingIntrastat.buildLink(),
                },
                {
                  label: t('submenu.cu_declarations'),
                  href: RouteConfig.BookkeepingCUDeclaration.buildLink(),
                },
                {
                  label: t('submenu.770'),
                  href: RouteConfig.BookkeepingDeclaration770.buildLink(),
                },
                {
                  label: t('submenu.vat_declaration'),
                  href: RouteConfig.BookkeepingVATDeclaration.buildLink(),
                },
                ...(hasBilancioCee
                  ? [
                      {
                        label: t('submenu.financial_statement'),
                        href: RouteConfig.BookkeepingFinancialStatement.buildLink(),
                      },
                    ]
                  : []),
              ],
            },
            {
              label: t('submenu.taxes_and_contributions'),
              items: [
                {
                  label: t('submenu.debits_credits'),
                  href: RouteConfig.BookkeepingTaxDebitCredits.buildLink(),
                },
                {
                  label: t('submenu.f24'),
                  href: RouteConfig.BookkeepingF24.buildLink(),
                },
              ],
            },
            {
              borderTop: true,
              items: [
                {
                  label: t('submenu.settings'),
                  href: RouteConfig.BookkeepingSettings.buildLink(),
                },
              ],
            },
          ],
    [t, isPayrollConsultant, hasBilancioCee],
  );

  const isActive = Boolean(useRouteMatch(sections.flatMap((s) => s.items.map((i) => i.href ?? ''))));

  if (!canAccess && !isPayrollConsultant) return null;

  return (
    <NavigationSidebarItem
      href={
        isPayrollConsultant
          ? RouteConfig.GeneralBookkeepingNonElectronicDocuments.buildLink()
          : RouteConfig.BookkeepingDocuments.buildLink()
      }
      iconComponent={FinancialAdvisorIcon}
      title={t('label')}
      isActive={isActive}
      isSubmenuOpen={isSubmenuOpen}
      onOpenSubmenu={openSubmenu}
      submenuSections={sections}
    />
  );
};

export default memo(BookkeepingItem);
